// src/components/device/DeviceCommandHistory.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DataGrid } from "@mui/x-data-grid";
import { useSocket } from "../../../socket/SocketContext";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

function StatusChip({ status }) {
  const s = (status || "pending").toLowerCase();
  const color =
    s === "executed" || s === "delivered" ? "success" :
    s === "failed" || s === "timeout" ? "error" :
    s === "queued" ? "warning" :
    "default";

  return (
    <Chip
      size="small"
      label={s.toUpperCase()}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: 700 }}
    />
  );
}

function DetailRow({ label, children }) {
  return (
    <Box sx={{ display: "grid", gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}

export default function DeviceCommandHistory({ deviceId, imei }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCmd, setSelectedCmd] = useState(null);
  const [error, setError] = useState("");
  const { on, subscribeToDevice, unsubscribeFromDevice } = useSocket();

  const fetchHistory = useCallback(async () => {
    if (!deviceId) return;
    try {
      setError("");
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${backendUrl}/commands/history/${deviceId}?limit=50&page=1`,
        { headers: { "x-auth-token": token } }
      );
      const commands = res?.data?.data?.commands;
      setRows(Array.isArray(commands) ? commands : []);
    } catch (e) {
      console.error("History fetch failed", e);
      setError(e?.response?.data?.message || "Failed to fetch command history");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchHistory();
    const i = setInterval(fetchHistory, 5000);
    return () => clearInterval(i);
  }, [fetchHistory]);

  useEffect(() => {
    const normalizedImei = String(imei || "").trim();
    if (!normalizedImei) return undefined;

    subscribeToDevice?.(normalizedImei);

    const applyRealtimeUpdate = (payload = {}) => {
      const payloadDeviceId = String(payload.deviceId || "").trim();
      const payloadImei = String(payload.imei || "").trim();
      const commandId = String(payload.commandId || "").trim();

      const matchesDevice =
        (payloadDeviceId && String(deviceId || "").trim() === payloadDeviceId) ||
        (payloadImei && normalizedImei === payloadImei);

      if (!matchesDevice) return;

      if (!commandId) {
        fetchHistory();
        return;
      }

      setRows((prev) => {
        let found = false;
        const next = prev.map((row) => {
          const rowCommandId = String(row?.responseData?.commandId || "").trim();
          if (rowCommandId !== commandId) return row;
          found = true;
          return {
            ...row,
            status: String(payload.status || row.status || "").toLowerCase(),
            response: payload.response || row.response || "",
            deliveredAt:
              String(payload.status || "").toUpperCase() === "DELIVERED"
                ? payload.timestamp || row.deliveredAt
                : row.deliveredAt,
            failedAt:
              String(payload.status || "").toUpperCase() === "FAILED"
                ? payload.timestamp || row.failedAt
                : row.failedAt,
            responseData: {
              ...(row.responseData || {}),
              ackPacketHex: payload.ackPacketHex || row?.responseData?.ackPacketHex || null,
              protocol: payload.protocol || row?.responseData?.protocol || null,
              serial: payload.serial ?? row?.responseData?.serial ?? null,
            },
          };
        });
        return found ? next : prev;
      });
    };

    const offSuccess = on?.("command:success", applyRealtimeUpdate);
    const offFailed = on?.("command:failed", applyRealtimeUpdate);
    const offQueued = on?.("command:queued", applyRealtimeUpdate);
    const offStatus = on?.("command:status", applyRealtimeUpdate);

    return () => {
      offSuccess?.();
      offFailed?.();
      offQueued?.();
      offStatus?.();
      unsubscribeFromDevice?.(normalizedImei);
    };
  }, [deviceId, imei, fetchHistory, on, subscribeToDevice, unsubscribeFromDevice]);

  useEffect(() => {
    if (!selectedCmd?._id) return;
    const fresh = rows.find((row) => String(row?._id) === String(selectedCmd._id));
    if (fresh) setSelectedCmd(fresh);
  }, [rows, selectedCmd]);

  const columns = useMemo(() => [
    {
      field: "sentAt",
      headerName: "Time",
      width: 190,
      valueGetter: (_, row) => row?.sentAt || row?.createdAt,
      renderCell: (params) => {
        const d = params.value ? new Date(params.value) : null;
        return (
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
            {d ? d.toLocaleString() : "-"}
          </Typography>
        );
      },
    },
    {
      field: "displayName",
      headerName: "Command",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {params.row?.displayName || params.row?.commandType || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row?.commandType || "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "response",
      headerName: "Response",
      minWidth: 220,
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        const normalizedStatus = String(row?.status || "").toLowerCase();
        const text =
          row?.response ||
          (normalizedStatus === "queued"
            ? "Waiting for device to reconnect..."
            : normalizedStatus === "pending" || normalizedStatus === "sent"
            ? "Waiting for device reply..."
            : "-");
        return (
          <Tooltip title={text || ""}>
            <Typography
              variant="body2"
              sx={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {text}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "initiatedBy",
      headerName: "By",
      width: 190,
      valueGetter: (_, row) => row?.initiatedBy?.username || row?.initiatedBy?.email || "system",
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          startIcon={<VisibilityIcon />}
          onClick={() => setSelectedCmd(params.row)}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
        >
          Details
        </Button>
      ),
    },
  ], []);

  const gridRows = useMemo(
    () => rows.map((r) => ({ id: r._id, ...r })),
    [rows]
  );

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ""));
    } catch (_) {}
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Command History</Typography>
          <Typography variant="body2" color="text.secondary">
            Auto refresh every 5 seconds • Last 50 commands
          </Typography>
        </Box>

        <Button variant="outlined" onClick={fetchHistory} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ width: "100%", height: 520 }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "sentAt", sort: "desc" }] },
          }}
          sx={{
            borderRadius: 3,
            "& .MuiDataGrid-columnHeaders": { fontWeight: 900 },
          }}
        />
      </Box>

      {/* Details Dialog */}
      <Dialog open={!!selectedCmd} onClose={() => setSelectedCmd(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Command Details
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                <StatusChip status={selectedCmd?.status} />
                {selectedCmd?.priority && (
                  <Chip size="small" label={`PRIORITY: ${String(selectedCmd.priority).toUpperCase()}`} variant="outlined" />
                )}
                {selectedCmd?.isDangerous && (
                  <Chip size="small" color="warning" label="DANGEROUS" />
                )}
                {selectedCmd?.expectResponse === false && (
                  <Chip size="small" label="NO RESPONSE EXPECTED" variant="outlined" />
                )}
              </Stack>
            </Box>

            <IconButton onClick={() => setSelectedCmd(null)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent>
          {selectedCmd && (
            <Box sx={{ display: "grid", gap: 2 }}>
              <DetailRow label="Command Name">
                <Typography variant="body1" sx={{ fontWeight: 800 }}>
                  {selectedCmd.displayName || "-"}
                </Typography>
              </DetailRow>

              <DetailRow label="Command Type">
                <Chip size="small" label={selectedCmd.commandType || "-"} variant="outlined" />
              </DetailRow>

              <DetailRow label="Raw Command">
                <Box
                  sx={{
                    bgcolor: "grey.100",
                    borderRadius: 2,
                    p: 1.25,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: 12,
                    wordBreak: "break-all",
                    position: "relative",
                  }}
                >
                  <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                    <Tooltip title="Copy">
                      <IconButton size="small" onClick={() => copy(selectedCmd.rawCommand)}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {selectedCmd.rawCommand || "-"}
                </Box>
              </DetailRow>

              <DetailRow label="Response">
                {(() => {
                  const normalizedStatus = String(selectedCmd.status || "").toLowerCase();
                  const hasResponse = !!selectedCmd.response;
                  const isWaiting =
                    normalizedStatus === "queued" ||
                    normalizedStatus === "pending" ||
                    normalizedStatus === "sent";
                  const palette = hasResponse
                    ? {
                        bg: "success.50",
                        border: "success.200",
                      }
                    : isWaiting
                    ? {
                        bg: "info.50",
                        border: "info.200",
                      }
                    : {
                        bg: "error.50",
                        border: "error.200",
                      };

                  return (
                <Box
                  sx={{
                    bgcolor: palette.bg,
                    border: "1px solid",
                    borderColor: palette.border,
                    borderRadius: 2,
                    p: 1.25,
                    fontSize: 13,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedCmd.response ||
                    ((selectedCmd.status === "queued" || selectedCmd.status === "pending" || selectedCmd.status === "sent")
                      ? "⏳ Waiting for device reply..."
                      : "❌ No response received")}
                </Box>
                  );
                })()}
              </DetailRow>

              {selectedCmd.responseData && Object.keys(selectedCmd.responseData).length > 0 && (
                <DetailRow label="Response Data">
                  <Box
                    sx={{
                      bgcolor: "grey.100",
                      borderRadius: 2,
                      p: 1.25,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                      overflow: "auto",
                      maxHeight: 240,
                      whiteSpace: "pre",
                    }}
                  >
                    {JSON.stringify(selectedCmd.responseData, null, 2)}
                  </Box>
                </DetailRow>
              )}

              {selectedCmd.parameters && Object.keys(selectedCmd.parameters).length > 0 && (
                <DetailRow label="Parameters">
                  <Box
                    sx={{
                      bgcolor: "grey.100",
                      borderRadius: 2,
                      p: 1.25,
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: 12,
                      overflow: "auto",
                      maxHeight: 240,
                      whiteSpace: "pre",
                    }}
                  >
                    {JSON.stringify(selectedCmd.parameters, null, 2)}
                  </Box>
                </DetailRow>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <DetailRow label="Sent At">
                  <Typography variant="body2">
                    {new Date(selectedCmd.sentAt || selectedCmd.createdAt).toLocaleString()}
                  </Typography>
                </DetailRow>

                {selectedCmd.executedAt && (
                  <DetailRow label="Executed At">
                    <Typography variant="body2">
                      {new Date(selectedCmd.executedAt).toLocaleString()}
                    </Typography>
                  </DetailRow>
                )}

                <DetailRow label="Initiated By">
                  <Typography variant="body2">
                    {selectedCmd.initiatedBy?.username || selectedCmd.initiatedBy?.email || "System"}
                  </Typography>
                </DetailRow>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <DetailRow label="Max Retries">
                  <Typography variant="body2">{selectedCmd.maxRetries ?? 3}</Typography>
                </DetailRow>
                <DetailRow label="Retry Count">
                  <Typography variant="body2">{selectedCmd.retryCount ?? 0}</Typography>
                </DetailRow>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
